// 3rd Party
import _, { get } from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';

// Components
import { Div, H1, H2, P } from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import {
  requestEmailVerification,
  requestPhoneVerification,
  requestPhoneVerificationSmsStatus,
  verifyEmail,
  verifyPhone,
} from 'connect/actions';
import {
  selectCJTags,
  selectDataClinic,
  selectDataEmailVerifiedAt,
  selectDataLoadingEmailVerification,
  selectDataLoadingPhoneVerification,
  selectDataLoadingPhoneVerificationRequest,
  selectDataLoadingPhoneVerificationSmsStatus,
  selectDataPatient,
  selectDataPhoneVerificationSmsStatus,
  selectDataPhoneVerificationSmsStatusError,
  selectDataPhoneVerifiedAt,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { Button } from 'opencare-crown';

import { useAnalyticsService } from 'services/AnalyticsService';
import cjAffiliate from 'utils/cjAffiliate';
import { usePrevious } from 'utils/customHooks/usePrevious';

import {
  GtmDataLayerEvent,
  trackGtmDataLayerEvent,
} from '../../../../../utils/analytics';
import Animate from '../../../../common/components/Animate/Animate';
import VerifyPhoneAndEmailOneSvg from '../../../../common/components/svgs/VerifyPhoneAndEmailOneSvg';
import VerifyPhoneAndEmailTwoSvg from '../../../../common/components/svgs/VerifyPhoneAndEmailTwoSvg';
import VerifyPhoneSvg from '../../../../common/components/svgs/VerifyPhoneSvg';

// Other

import { prettyPrintPhoneNumber } from '../../../../common/helpers/verify-step-helpers';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import {
  StepLoading,
  StyledInput,
  StyledLock,
} from '../../../checkout-styled-components';
import {
  EMAIL,
  PHONE,
  SMS_DELIVERED_STATUS,
  SMS_FAILED_STATUS,
} from '../../../helpers/checkout-constants';

import { Centered, Highlight, StyledSpin } from './styled-components';

const analytics = new Analytics();

// Component
const VerifyStep = ({ handleNextStep, state }) => {
  const dispatch = useDispatch();
  const funnelStorage = useFunnelStorage();

  const analyticsService = useAnalyticsService();

  const user = useSelector(selectDataUser);
  const clinic = useSelector(selectDataClinic);
  const patient = useSelector(selectDataPatient);
  const emailVerifiedAt = useSelector(selectDataEmailVerifiedAt);
  const previousEmailVerifiedAt = usePrevious(emailVerifiedAt);
  const phoneVerifiedAt = useSelector(selectDataPhoneVerifiedAt);
  const previousPhoneVerifiedAt = usePrevious(phoneVerifiedAt);
  const loadingEmailVerification = useSelector(
    selectDataLoadingEmailVerification,
  );
  const loadingPhoneVerification = useSelector(
    selectDataLoadingPhoneVerification,
  );
  const loadingPhoneVerificationRequest = useSelector(
    selectDataLoadingPhoneVerificationRequest,
  );
  const loadingPhoneVerificationSmsStatus = useSelector(
    selectDataLoadingPhoneVerificationSmsStatus,
  );
  const phoneVerificationSmsStatus = useSelector(
    selectDataPhoneVerificationSmsStatus,
  );
  const phoneVerificationSmsStatusError = useSelector(
    selectDataPhoneVerificationSmsStatusError,
  );
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const cjTags = useSelector(selectCJTags);

  const [code, setCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [smsDeliveryFailed, setSmsDeliveryFailed] = useState(false);
  const [verifyStep, setVerifyStep] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [
    smsStatusVerificationAttempts,
    setSmsStatusVerificationAttempts,
  ] = useState(0);

  // All necessary data is loaded
  useEffect(() => {
    if (user && patient && clinic) {
      setIsLoading(false);
    }
  }, [user, patient, clinic]);

  // Get state based on what the patient has verified
  useEffect(() => {
    if (!phoneVerifiedAt) {
      dispatch(requestPhoneVerification('text'));
      setVerifyStep(PHONE);
    } else if (!emailVerifiedAt) {
      setCode('');
      dispatch(requestEmailVerification());
      setVerifyStep(EMAIL);
    } else if (emailVerifiedAt && phoneVerifiedAt) {
      trackGtmDataLayerEvent(
        GtmDataLayerEvent.APPOINTMENT_CHECKOUT_ACCOUNT_VERIFIED,
      );
      cjAffiliate.trackPatientConversion(patient, get(cjTags, 'event'));
      handleNextStep();
    }
  }, [emailVerifiedAt, phoneVerifiedAt]);

  // Auto verify once full code is entered
  useEffect(() => {
    if (code.length === 4) {
      if (verifyStep === PHONE) {
        dispatch(verifyPhone(code));
      } else {
        dispatch(verifyEmail(code));
      }
    }
  }, [code]);

  // Handle verification errors
  useEffect(() => {
    if (
      !loadingEmailVerification
      && !loadingPhoneVerification
      && code.length === 4
    ) {
      setCode('');
      setHasError(true);
    }
  }, [loadingEmailVerification, loadingPhoneVerification]);

  // Request SMS status once phone verification code is sent
  useEffect(() => {
    if (
      verifyStep === PHONE
      && !loadingPhoneVerificationRequest
      && !phoneVerificationSmsStatus
      && !loadingPhoneVerificationSmsStatus
    ) {
      setTimeout(() => {
        dispatch(requestPhoneVerificationSmsStatus());
      }, 2000);
    }
  }, [verifyStep, loadingPhoneVerificationRequest]);

  // Handle phone verification sms status
  useEffect(() => {
    if (!phoneVerificationSmsStatus) return;

    if (SMS_FAILED_STATUS.includes(phoneVerificationSmsStatus.status)) {
      dispatch(requestPhoneVerification('call'));
      setSmsDeliveryFailed(true);
    }
  }, [phoneVerificationSmsStatus]);

  useEffect(() => {
    if (!phoneVerificationSmsStatusError && !phoneVerificationSmsStatus) return;

    if (
      smsStatusVerificationAttempts < 3
      && (phoneVerificationSmsStatusError
      || phoneVerificationSmsStatus.status !== SMS_DELIVERED_STATUS)
    ) {
      setSmsStatusVerificationAttempts(smsStatusVerificationAttempts + 1);
      setTimeout(() => {
        dispatch(requestPhoneVerificationSmsStatus());
      }, 2000);
    }
  }, [phoneVerificationSmsStatus, phoneVerificationSmsStatusError]);

  // analytics page view event
  useEffect(() => {
    if (verifyStep) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-account',
        name: 'form-booking',
        section: `verify-${verifyStep}`,
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        ...analyticsService.analyticsData,
      });
    }
  }, [verifyStep]);

  // analytics verified event
  useEffect(() => {
    if (verifyStep) {
      let stepIsVerified = null;

      if (emailVerifiedAt && !previousEmailVerifiedAt) {
        stepIsVerified = EMAIL;
      }

      if (phoneVerifiedAt && !previousPhoneVerifiedAt) {
        stepIsVerified = PHONE;
      }

      if (stepIsVerified) {
        analytics.trackVerified({
          pageType: analytics.pageTypes.FORM,
          pageName: 'form-booking-account',
          name: 'form-booking',
          section: `verify-${stepIsVerified}`,
          practiceId: requestedAppointment.provider?.clinic,
          providerId: requestedAppointment.providerId,
          userId: _.get(user, 'id'),
          patientId: _.get(patient, 'id'),
          funnelConfigId: _.get(funnelStorage, 'config.id'),
        });
      }
    }
  }, [emailVerifiedAt, phoneVerifiedAt]);

  const handleCodeChange = (e) => {
    const enteredCode = e.target.value;
    if (enteredCode.length <= 4) {
      setCode(e.target.value);
      if (hasError) {
        setHasError(false);
      }
    }
  };

  const requestVerificationAgain = () => {
    if (verifyStep === PHONE) {
      dispatch(requestPhoneVerification('text'));
    } else {
      dispatch(requestEmailVerification());
    }
  };

  const verifiedMessage = () => {
    let message = 'phone';

    if (!emailVerifiedAt) {
      message += ' and email';
    }

    return message;
  };

  const verifiedIcon = () => {
    // Check if they have to verify one or both
    let numberOfSteps = 1;

    if (!emailVerifiedAt) {
      numberOfSteps = 2;
    }

    // Determine which icon to render
    if (verifyStep === PHONE && numberOfSteps === 1) {
      return <VerifyPhoneSvg />;
    }

    if (verifyStep === PHONE && numberOfSteps === 2) {
      return <VerifyPhoneAndEmailOneSvg />;
    }

    return <VerifyPhoneAndEmailTwoSvg />;
  };

  if (isLoading) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  return (
    <div>
      <H1 fontSize="20px" margin="-12px 0 0 auto">
        Keep your account secure
        <StyledLock
          aria-hidden
          bottom="5px"
          margin="0 0 0 8px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </H1>
      <P color={colors.slate} margin="0 0 10px">
        To ensure you're the only one who can access your account, we need to
        verify your {verifiedMessage()}.
      </P>
      <Centered padding="20px 10px 10px 10px">
        {verifiedIcon()}
        <div id="label">
          <P color={colors.slate} margin="10px 0 0 0">
            Enter the code we sent to:
          </P>
          <H2
            color={colors.sageDark}
            fontSize="16px"
            className="sensitive-data"
            data-testid="verify-location"
          >
            {verifyStep === PHONE
              ? prettyPrintPhoneNumber(state.mobilePhone)
              : state.email}
          </H2>
        </div>
        <StyledSpin
          color={colors.sageDark}
          spinning={loadingEmailVerification || loadingPhoneVerification}
        >
          <StyledInput
            fontSize="22px"
            onChange={handleCodeChange}
            textalign="center"
            value={code}
            width="300px"
            aria-labelledby="label"
            data-testid="input"
            disabled={loadingEmailVerification || loadingPhoneVerification}
          />
        </StyledSpin>
        <Animate maxHeight="45px" show={hasError}>
          <Div margin="20px 0 0 0">
            <P
              tabIndex="-1"
              ref={(elem) => {
                if (elem && hasError) {
                  elem.focus();
                }
              }}
              color="red"
            >
              The code you entered is incorrect.
            </P>
          </Div>
        </Animate>
        {verifyStep === PHONE
          ? (
            <Div margin="20px 0 0 0">
              {smsDeliveryFailed
              && (
                <H2
                  color={colors.sageDark}
                  fontSize="16px"
                  lineHeight="16px"
                  margin="0 0 20px"
                >
                  We have a delivery issue. Please expect a verification call
                  within the next 5 seconds.
                </H2>
              )}
              <Button
                type="secondary"
                shape="round"
                onClick={() => dispatch(requestPhoneVerification('call'))}
                disabled={loadingEmailVerification || loadingPhoneVerification}
              >
                <span>
                  Didn't receive anything?{' '}
                  <Highlight>
                    Call me {smsDeliveryFailed ? 'again' : 'instead'}
                  </Highlight>
                </span>
              </Button>
            </Div>
          )

          : (
            <>
              <Div margin="20px 0 0 0">
                <Button
                  type="secondary"
                  shape="round"
                  onClick={requestVerificationAgain}
                  disabled={loadingEmailVerification || loadingPhoneVerification}
                >
                  Didn't receive anything? Send a new email
                </Button>
              </Div>
              <P color={colors.slate} margin="10px 0 0 0">
                <strong>Remember to check your spam folder</strong>
              </P>
            </>
          )}
      </Centered>
    </div>
  );
};

VerifyStep.propTypes = {
  handleNextStep: PropTypes.func,
  state: PropTypes.object,
};

export default VerifyStep;
